import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { EmailIcon } from "@offstreetparking/design-system/icons/EmailIcon";
import { PhoneIcon } from "@offstreetparking/design-system/icons/PhoneIcon";
import { Heading, Link, Text } from "@offstreetparking/design-system/typography";
import { appConfig } from "../../../app.config";
import { useSupportUrl } from "../../../hooks/useSupportUrl";
import { TERMS_URL } from "../../../lib/legal-urls";
import { useKiosk } from "../../providers/KioskProvider";
type ContactProps = {
	name: string;
	email?: string | null;
	phoneNumber?: string | null;
	formattedPhoneNumber?: string | null;
	supportText?: string;
	hideOffstreetSupport: boolean;
	locationId?: string;
	eventId?: string;
};
export const Contact = ({
	email,
	phoneNumber,
	formattedPhoneNumber,
	supportText,
	hideOffstreetSupport = false,
	locationId,
	eventId,
}: ContactProps) => {
	const { isKiosk } = useKiosk();
	const { t } = useLingui();
	const { supportUrl } = useSupportUrl({ locationId, eventId, ticketForm: "general_parker_support" });
	return (
		<div className="flex flex-col gap-4 pb-24 md:pb-0">
			<Heading As="h2" kind="h3">
				{t`Help and Support`}
			</Heading>
			{(email || (phoneNumber && formattedPhoneNumber)) && (
				<div className="space-y-4">
					<Text size="sm">
						{supportText ||
							t`To dispute a parking violation or for parking operations questions, please contact:`}
					</Text>

					{email && (
						<Text size="sm" intent="muted" className="flex items-center gap-2 pl-4">
							<AccessibleIcon>
								<EmailIcon className="size-4" />
							</AccessibleIcon>
							{isKiosk ? (
								<Text As="span" size="sm">
									{email}
								</Text>
							) : (
								<Link size="sm" href={`mailto:${email}`} aria-label={t`Send email to ${email}`}>
									{email}
								</Link>
							)}
						</Text>
					)}

					{phoneNumber && formattedPhoneNumber && (
						<Text size="sm" intent="muted" className="flex items-center gap-2 pl-4">
							<AccessibleIcon>
								<PhoneIcon className="size-4" />
							</AccessibleIcon>
							{isKiosk ? (
								<Text As="span" size="sm">
									{formattedPhoneNumber}
								</Text>
							) : (
								<Link
									size="sm"
									href={`tel:${phoneNumber}`}
									aria-label={t`Call ${formattedPhoneNumber}`}
								>
									{formattedPhoneNumber}
								</Link>
							)}
						</Text>
					)}
				</div>
			)}
			{!hideOffstreetSupport && (
				<div className="space-y-2">
					<Text size="sm">{t`For product support:`}</Text>
					<Text size="sm" intent="muted" className="flex items-center gap-2 pl-4">
						<AccessibleIcon>
							<EmailIcon className="size-4" />
						</AccessibleIcon>
						{isKiosk ? (
							<Text As="span" size="sm">
								{appConfig.HELP_CENTER_URL}
							</Text>
						) : (
							<Link
								size="sm"
								href={supportUrl}
								isExternal
								aria-label={`${t`Contact Us`} ${t`(opens in a new tab)`}`}
							>
								{t`Contact Us`}
							</Link>
						)}
					</Text>
				</div>
			)}

			<div>
				{isKiosk ? (
					<Text As="span" size="sm" className="md:hidden">
						{t`Terms of service`}
					</Text>
				) : (
					<Link
						size="sm"
						className="md:hidden"
						href={TERMS_URL}
						isExternal
						aria-label={`${t`Terms of service`} ${t`(opens in a new tab)`}`}
					>
						{t`Terms of service`}
					</Link>
				)}
			</div>
		</div>
	);
};
